import { CONTRACT, ContractEventParams, useContract } from '@apeiron/contract'
import { CurrencyTokenType } from '@apeiron/currency'
import {
  CustomError,
  ERROR_CODE,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling } from '@apeiron/wallet'
import useAllowCurrency from '@src/hooks/contract/useAllowCurrency'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const useMakeupCheckIn = () => {
  const { sendWithOption } = useContract(
    CONTRACT.DAILY_CHECK_IN.MAKE_UP_CHECK_IN
  )

  const { getWalletAccount } = useWeb3()

  const { t } = useTranslation()

  const { setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  const { requestCurrency } = useAllowCurrency()

  const makeupCheckIn = useCallback(
    async (param: MakeupCheckinParam): Promise<boolean> => {
      try {
        setInfo({
          message: t('common.wait_token_approve')
        })

        const walletAccount = await getWalletAccount()

        if (!walletAccount.address) {
          throw new CustomError('cannot get wallet address')
        }

        const approved = await requestCurrency(
          walletAccount.address,
          CONTRACT.DAILY_CHECK_IN.MAKE_UP_CHECK_IN.address.RONIN,
          CurrencyTokenType.Anima,
          BigInt(param.neededAnima)
        )

        if (!approved) {
          throw new CustomError(
            'cannot approve anima token for makeup check in',
            ERROR_CODE.CONTRACT.APPROVAL_FAIL
          )
        }

        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t('common.popup.transaction.be_patient')}
            />
          )
        })

        await sendWithOption(
          {
            verifyEvent: ContractEventParams.MakeupCheckin
          },
          param.campaignID,
          param.maxMakeupCount,
          param.makeupDayCount,
          param.animusUse,
          param.signTimeInSec,
          param.signature
        )

        setInfo(null)

        return true
      } catch (error) {
        showErrorPopup(error)

        return false
      }
    },
    [
      setInfo,
      t,
      getWalletAccount,
      requestCurrency,
      sendWithOption,
      showErrorPopup
    ]
  )

  return {
    makeupCheckIn
  }
}

type MakeupCheckinParam = {
  campaignID: string
  maxMakeupCount: number
  makeupDayCount: number
  animusUse: string
  signTimeInSec: string
  signature: string
  neededAnima: string
}

export default useMakeupCheckIn
